import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getNotifications, markAllNotificationsRead, markNotificationRead } from "@/lib/api";

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getNotifications();
      setItems(data);
    } catch (_e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const unread = items.filter((n) => !n.read).length;

  const handleOpen = () => {
    setOpen((v) => !v);
    if (!open) load();
  };

  const handleClick = async (n) => {
    try {
      if (!n.read) await markNotificationRead(n.notification_id);
    } catch (_e) {}
    setOpen(false);
    setItems((prev) =>
      prev.map((x) => (x.notification_id === n.notification_id ? { ...x, read: true } : x))
    );
    if (n.challenge_id) {
      navigate(`/challenge/${n.challenge_id}`);
    }
  };

  const handleMarkAll = async () => {
    try {
      await markAllNotificationsRead();
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (_e) {}
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleOpen}
        data-testid="notification-bell"
        className="cm-press relative inline-flex items-center justify-center border-2 border-[#101010] bg-white w-9 h-9 rounded-md cm-shadow-sm"
        aria-label="Notificaciones"
      >
        <span className="text-base">🔔</span>
        {unread > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF4C4C] text-white text-[10px] font-bold border-2 border-[#101010] flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 cursor-default"
            aria-label="Cerrar notificaciones"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 top-full mt-2 z-50 w-[min(100vw-2rem,22rem)] bg-white border-4 border-[#101010] rounded-xl cm-shadow overflow-hidden">
            <div className="bg-[#101010] px-4 py-2 flex items-center justify-between">
              <span className="font-mono-cm text-[10px] uppercase tracking-widest text-white/80">
                Notificaciones
              </span>
              {unread > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAll}
                  className="font-mono-cm text-[9px] uppercase tracking-widest text-[#FFDE59] hover:underline"
                >
                  Marcar leídas
                </button>
              )}
            </div>
            <div className="max-h-80 overflow-y-auto">
              {loading && items.length === 0 ? (
                <p className="p-4 font-mono-cm text-xs text-[#101010]/50 uppercase tracking-widest text-center">
                  Cargando...
                </p>
              ) : items.length === 0 ? (
                <p className="p-4 font-mono-cm text-xs text-[#101010]/50 uppercase tracking-widest text-center">
                  Nada por acá
                </p>
              ) : (
                items.map((n) => (
                  <button
                    key={n.notification_id}
                    type="button"
                    onClick={() => handleClick(n)}
                    className={`w-full text-left px-4 py-3 border-b-2 border-[#101010]/10 hover:bg-[#FFDE59]/30 transition-colors ${
                      !n.read ? "bg-[#FFDE59]/20" : ""
                    }`}
                  >
                    <div className="font-display uppercase text-sm leading-tight">{n.title}</div>
                    <div className="font-mono-cm text-[10px] text-[#101010]/60 mt-1 leading-snug">{n.body}</div>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
